import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  MapPin, Calendar, Clock, DollarSign, Info, Navigation,
  Truck, ShieldCheck, CheckCircle, ChevronDown, Users,
  Star, Loader2, X, Lock, Eye, TrendingUp, Award, Zap, Heart
} from "lucide-react";

import RouteRow from "../Includes/RouteRow";
import useSavedLoads from "../hooks/useSavedLoads";

// ─── ACCEPTED / OFFERED JOBS (inlined) ─────────
const MY_JOBS_KEY = "driver_my_jobs"; 

function readMyJobs() {
  try {
    const raw = localStorage.getItem(MY_JOBS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeMyJobs(map) {
  try {
    localStorage.setItem(MY_JOBS_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable — fail silently */
  }
}

function useMyJobs() {
  const [jobsMap, setJobsMap] = useState(readMyJobs);

  useEffect(() => {
    const onStorage = (e) => { if (e.key === MY_JOBS_KEY) setJobsMap(readMyJobs()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const upsertJob = (load, job_status) => {
    const id = load.pk ?? load.id;
    setJobsMap((prev) => {
      const next = { ...prev, [id]: { ...prev[id], ...load, job_status, updated_at: new Date().toISOString() } };
      writeMyJobs(next);
      return next;
    });
  };

  const getJobStatus = (loadId) => jobsMap[loadId]?.job_status || null;

  return { upsertJob, getJobStatus };
}

// ─── ROUTE MAP COMPONENT ──────────────────────────────────────────────────────
function RouteMap({ origin, destination, originCoords, destCoords, meetingCoords, meetingName }) {
  const fromCoords = originCoords || [39.8283, -98.5795];
  const toCoords   = destCoords || [39.8283, -98.5795];

  const midLat  = (fromCoords[0] + toCoords[0]) / 2;
  const midLng  = (fromCoords[1] + toCoords[1]) / 2;
  const dLat    = toCoords[0] - fromCoords[0];
  const dLng    = toCoords[1] - fromCoords[1];
  const distMi  = Math.round(Math.sqrt(dLat * dLat + dLng * dLng) * 69);
  const zoom = distMi < 80 ? 8 : distMi < 200 ? 7 : distMi < 400 ? 6 : 5;

  const mapHtml = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
<style>*{margin:0;padding:0;box-sizing:border-box;}html,body,#map{width:100%;height:100%;}</style>
</head><body>
<div id="map"></div>
<script>
var map = L.map('map', {zoomControl:true, attributionControl:false}).setView([${midLat},${midLng}],${zoom});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:18}).addTo(map);

var fromPt = [${fromCoords[0]},${fromCoords[1]}];
var toPt   = [${toCoords[0]},${toCoords[1]}];
var dLat = toPt[0] - fromPt[0];
var dLng = toPt[1] - fromPt[1];
var pts = [];
var steps = 20;
for(var i=0;i<=steps;i++){
  var t = i/steps;
  var cx = (fromPt[1]+toPt[1])/2 + dLat*0.15;
  var cy = (fromPt[0]+toPt[0])/2 - dLng*0.15;
  var lat = (1-t)*(1-t)*fromPt[0] + 2*(1-t)*t*cy + t*t*toPt[0];
  var lng = (1-t)*(1-t)*fromPt[1] + 2*(1-t)*t*cx + t*t*toPt[1];
  pts.push([lat,lng]);
}

L.polyline(pts,{color:'#0f766e',weight:4,opacity:0.9}).addTo(map);
L.polyline(pts,{color:'#0f766e',weight:8,opacity:0.15}).addTo(map);

var originIcon = L.divIcon({html:'<div style="background:#d97706;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',iconAnchor:[7,7],className:''});
var destIcon   = L.divIcon({html:'<div style="background:#0f766e;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"></div>',iconAnchor:[9,9],className:''});

L.marker(fromPt,{icon:originIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${origin.replace(/'/g, "\\'")}</b>',{permanent:false,direction:'top'});
L.marker(toPt,  {icon:destIcon  }).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${destination.replace(/'/g, "\\'")}</b>',{permanent:false,direction:'top'});

${meetingCoords && meetingCoords[0] ? `
var meetingPt = [${meetingCoords[0]},${meetingCoords[1]}];
var meetIcon = L.divIcon({html:'<div style="background:#7c3aed;width:14px;height:14px;border:3px solid white;border-radius:3px;box-shadow:0 2px 6px rgba(0,0,0,0.4);transform:rotate(45deg);"></div>',iconAnchor:[7,7],className:''});
var meetName = "${meetingName ? meetingName.split(",")[0].replace(/"/g, '\\"').replace(/'/g, "\\'") : 'Meeting Point'}";
L.marker(meetingPt,{icon:meetIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">🚩 ' + meetName + '</b>',{permanent:true,direction:'top', offset:[0,-10]});
var bounds = L.latLngBounds([fromPt, toPt, meetingPt]);
map.fitBounds(bounds, {padding: [30, 30]});
` : ''}
</script>
</body></html>`;

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50 h-52 relative w-full">
      <iframe srcDoc={mapHtml} style={{ width: "100%", height: "100%", border: "none", display: "block" }} title="Route Map" />
      <div className="absolute bottom-2 left-2 right-2 bg-white/95 backdrop-blur border border-slate-200/60 rounded-xl px-3 py-2 flex items-center justify-between pointer-events-none shadow-sm">
        <span className="text-[11px] font-bold text-slate-700 truncate max-w-[38%]">📍 {origin.split(",")[0]}</span>
        <span className="text-[11px] font-bold text-teal-700">~{distMi} mi</span>
        <span className="text-[11px] font-bold text-slate-700 truncate max-w-[38%] text-right">🏁 {destination.split(",")[0]}</span>
      </div>
    </div>
  );
}

// ─── SMALL UI HELPERS ─────────────────────────────────────────────────────────
function RequirementChip({ icon: Icon, label, tone = "slate" }) {
  const tones = {
    amber:  "bg-amber-50 text-amber-800 border-amber-200",
    rose:   "bg-rose-50 text-rose-800 border-rose-200",
    violet: "bg-violet-50 text-violet-800 border-violet-200",
    slate:  "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border ${tones[tone]}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );
}

function ShipperIdentityBlock({ load, revealed }) {
  if (revealed) {
    return (
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#1a2744] text-white flex items-center justify-center font-black text-sm shrink-0">
          {load.company?.slice(0, 2).toUpperCase() || "CO"}
        </div>
        <div>
          <p className="text-sm font-black text-slate-900 leading-tight">{load.company}</p>
          <p className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Contract confirmed — details unlocked
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center shrink-0 border border-slate-200">
        <Lock className="w-4 h-4" />
      </div>
      <div>
        <p className="text-sm font-black text-slate-900 leading-tight">Verified Shipper</p>
        <p className="text-xs font-semibold text-slate-500 flex items-center gap-1">
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {load.shipper_rating || "4.8"} rating · {load.shipper_completed_loads || 120}+ loads posted
        </p>
      </div>
    </div>
  );
}

// ─── LOAD ROW (list item, expands in place) ───────────────────────────────────
function LoadRow({ load, isMatched, isUrgent, isRevealed, saved, jobStatus, onToggleSave, expanded, onToggleExpand, onSubmitOffer, onAcceptJob, busy }) {
  const id = load.pk ?? load.id;

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-colors ${expanded ? "border-teal-300" : "border-slate-200"}`}>
      <button onClick={onToggleExpand} className="w-full text-left px-4 sm:px-5 py-4 flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-black text-slate-900 text-base">#{load.id}</span>
            {isUrgent && (
              <span className="bg-red-50 text-red-600 text-[10px] font-black px-2 py-0.5 rounded-md border border-red-100 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Urgent
              </span>
            )}
            {isMatched && (
              <span className="bg-amber-100 text-amber-800 text-[10px] font-black px-2 py-0.5 rounded-md border border-amber-200 flex items-center gap-1">
                <Award className="w-3 h-3" /> Priority Match
              </span>
            )}
            {jobStatus === "offer_sent" && (
              <span className="bg-teal-50 text-teal-700 text-[10px] font-black px-2 py-0.5 rounded-md border border-teal-200">Offer Sent</span>
            )}
          </div>
          <p className="text-sm text-slate-500 font-semibold truncate">
            {load.from?.split(",")[0]} → {load.to?.split(",")[0]} · {load.commodity || "Oversize Load"}
          </p>
          <p className="text-xs text-slate-400 font-medium mt-0.5 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {load.date || "TBD"}
            <span className="mx-1">·</span>
            <Users className="w-3 h-3" /> {load.pending_offers ?? 0} offer{(load.pending_offers ?? 0) === 1 ? "" : "s"} pending
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-lg font-black text-emerald-600 leading-tight">{load.pay}</p>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{load.pay_type || "Flat"}</p>
          </div>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onToggleSave(); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onToggleSave(); } }}
            aria-label={saved ? "Remove from saved loads" : "Save load"}
            className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center hover:bg-rose-50 hover:border-rose-200 transition-colors"
          >
            <Heart className={`w-4 h-4 ${saved ? "fill-rose-500 text-rose-500" : "text-slate-400"}`} />
          </span>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4 sm:p-6 space-y-5 bg-slate-50/40">
          <RouteMap
            origin={load.from}
            destination={load.to}
            originCoords={load.startCoords}
            destCoords={load.endCoords}
            meetingCoords={load.meetingCoords}
            meetingName={load.meeting_point}
          />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white rounded-2xl p-4 border border-slate-200">
            <ShipperIdentityBlock load={load} revealed={isRevealed} />
            <div className="text-left sm:text-right">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Pay range</p>
              <p className="text-2xl font-black text-emerald-600">{load.pay}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white p-3.5 rounded-xl border border-slate-200">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Commodity</p>
              <p className="font-bold text-slate-900 text-sm truncate">{load.commodity || "N/A"}</p>
            </div>
            <div className="bg-white p-3.5 rounded-xl border border-slate-200">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Dimensions</p>
              <p className="font-bold text-slate-900 text-sm truncate">{load.dims || "Standard"}</p>
            </div>
            <div className="bg-white p-3.5 rounded-xl border border-slate-200">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Weight</p>
              <p className="font-bold text-slate-900 text-sm truncate">{load.weight || "N/A"}</p>
            </div>
            <div className="bg-white p-3.5 rounded-xl border border-slate-200">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Escorts</p>
              <p className="font-bold text-slate-900 text-sm">{load.escorts || 1} Vehicles</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {load.needs_front_escort && <RequirementChip icon={Truck} label="Front Escort" tone="amber" />}
            {load.needs_chase_escort && <RequirementChip icon={Truck} label="Chase Escort" tone="amber" />}
            {load.needs_height_pole && <RequirementChip icon={MapPin} label="Height Pole Required" tone="rose" />}
            {load.needs_steersman && <RequirementChip icon={Navigation} label="Steersman" tone="violet" />}
            {!load.needs_front_escort && !load.needs_chase_escort && !load.needs_height_pole && !load.needs_steersman && (
              <RequirementChip icon={CheckCircle} label="Standard pilot car duties" tone="slate" />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5 text-emerald-500" /> Pay structure
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500 font-semibold">Mileage rate</span><span className="font-bold text-slate-900">{load.mileage_rate ? `$${load.mileage_rate}/mi` : "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 font-semibold">Overnight pay</span><span className="font-bold text-slate-900">{load.overnight ? "Included" : "None"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 font-semibold">Terms</span><span className="font-bold text-slate-900">{load.payment_terms || "Standard"}</span></div>
              </div>
            </div>
            {load.meeting_point && (
              <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4">
                <h3 className="text-xs font-black text-violet-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-violet-500" /> Meeting point
                </h3>
                <p className="font-bold text-slate-900 text-sm">{load.meeting_point}</p>
                <p className="text-xs font-semibold text-slate-600">{load.meeting_city}, {load.meeting_state} {load.meeting_zip}</p>
              </div>
            )}
          </div>

          {load.notes && (
            <div className="bg-teal-50/60 p-4 rounded-2xl border border-teal-100 relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-teal-400 rounded-l-2xl" />
              <p className="text-xs font-black text-teal-900 uppercase tracking-widest mb-1 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Notes from the shipper</p>
              <p className="text-sm font-medium text-slate-700 leading-relaxed">{load.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            {jobStatus === "offer_sent" ? (
              <span className="px-5 py-2.5 rounded-xl font-bold text-teal-700 bg-teal-50 border border-teal-200 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Offer submitted
              </span>
            ) : isMatched ? (
              <>
                <button className="px-4 py-2.5 rounded-xl font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition-colors">
                  Decline
                </button>
                <button
                  disabled={busy}
                  onClick={() => onAcceptJob(load)}
                  className="px-5 py-2.5 rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/20 flex items-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Accept Job
                </button>
              </>
            ) : (
              <button
                onClick={() => onSubmitOffer(load)}
                className="px-6 py-2.5 rounded-xl font-bold text-white bg-[#1a2744] hover:bg-blue-900 transition-colors shadow-lg shadow-[#1a2744]/20"
              >
                Submit Offer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN DRIVER LOAD BOARD PAGE ──────────────────────────────────────────────
export default function LoadBoardpage() {
  const navigate = useNavigate();

  // Grab user from the Layout's Outlet context
  const { user } = useOutletContext() || {};

  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [filter, setFilter] = useState("all"); 
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const { isSaved, toggleSave, autoSave, setAutoSave, autoSaveIncoming } = useSavedLoads();
  const { upsertJob, getJobStatus } = useMyJobs();

  const [offerLoad, setOfferLoad] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appForm, setAppForm] = useState({
    is_counter_offer: false,
    bid_amount: "",
    estimated_eta: "",
    driver_notes: "",
    confirms_height_pole: false,
    confirms_commercial_insurance: false
  });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const fetchLoads = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
        const token  = localStorage.getItem("user_token") || localStorage.getItem("access_token");

        const response = await fetch(`${apiUrl}/api/loads/`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();

          const sortedLoads = data.sort((a, b) => {
            const aMatched = a.invited_driver_ids?.includes(user?.id) ? 1 : 0;
            const bMatched = b.invited_driver_ids?.includes(user?.id) ? 1 : 0;
            if (aMatched !== bMatched) return bMatched - aMatched;

            const aUrgent = a.status === 'Urgent' ? 1 : 0;
            const bUrgent = b.status === 'Urgent' ? 1 : 0;
            return bUrgent - aUrgent;
          });

          setLoads(sortedLoads);
          autoSaveIncoming(sortedLoads, user?.id);
        }
      } catch (error) {
        console.error("Failed to fetch loads", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchLoads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openOfferModal = (load) => {
    setOfferLoad(load);
    setAppForm({
      is_counter_offer: false,
      bid_amount: "",
      estimated_eta: "",
      driver_notes: "",
      confirms_height_pole: false,
      confirms_commercial_insurance: false
    });
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!offerLoad) return;
    const loadId = offerLoad.pk ?? offerLoad.id;
    setIsSubmitting(true);

    upsertJob({ ...offerLoad, has_applied: true }, "offer_sent");
    setLoads((prev) => prev.map((l) => (l.pk ?? l.id) === loadId ? { ...l, has_applied: true } : l));

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const token  = localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const res = await fetch(`${apiUrl}/api/loads/${loadId}/apply/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(appForm)
      });
      if (!res.ok) console.warn("Offer saved locally; server sync failed and will retry later.");
    } catch (err) {
      console.warn("Offer saved locally; no network to sync with the server.", err);
    } finally {
      setIsSubmitting(false);
      setOfferLoad(null);
      setToast({ type: "success", message: "Offer submitted — track it under My Jobs." });
    }
  };

  const handleAcceptJob = async (load) => {
    const loadId = load.pk ?? load.id;
    setBusyId(loadId);

    upsertJob(load, "upcoming");
    setLoads((prev) => prev.filter((l) => (l.pk ?? l.id) !== loadId));
    setExpandedIds((prev) => { const next = new Set(prev); next.delete(loadId); return next; });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const token  = localStorage.getItem("user_token") || localStorage.getItem("access_token");
      const res = await fetch(`${apiUrl}/api/loads/${loadId}/apply/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          is_counter_offer: false,
          driver_notes: "Accepted direct job offer.",
          confirms_commercial_insurance: true,
          confirms_height_pole: true
        })
      });
      if (!res.ok) console.warn("Job accepted locally; server sync failed and will retry later.");
    } catch (err) {
      console.warn("Job accepted locally; no network to sync with the server.", err);
    } finally {
      setBusyId(null);
      setToast({ type: "success", message: "Job accepted! Find it under My Jobs." });
    }
  };

  const filteredLoads = loads.filter((load) => {
    if (filter === "matched") return load.invited_driver_ids?.includes(user?.id);
    if (filter === "urgent") return load.status === 'Urgent';
    return true;
  });

  const matchedCount = loads.filter(l => l.invited_driver_ids?.includes(user?.id)).length;
  const isIdentityRevealed = (load) => !!load?.is_awarded && !!load?.has_applied;

  return (
    <div style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      
      {/* 
        👇 FIX: Negative margins (-mt-5, -mx-3, sm:-mx-6) pull this header out of the Layout padding.
        This forces it to touch the TopBar and stretch to the edges, restoring the original look! 
      */}
      <div className="-mt-5 -mx-3 sm:-mx-6 px-3 sm:px-6 py-4 sm:py-5 bg-white border-b border-slate-200 mb-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900">Available Loads</h1>
          <p className="text-sm text-slate-500 font-medium mt-0.5 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Shipper details are revealed after a job is awarded to you.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          <button
            onClick={() => setAutoSave(!autoSave)}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm font-bold transition-colors ${
              autoSave ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
            }`}
            title="Automatically save Urgent and Priority Match loads"
          >
            <Heart className={`w-4 h-4 ${autoSave ? "fill-rose-500 text-rose-500" : "text-slate-400"}`} />
            Auto-save
            <span className={`ml-1 w-8 h-[18px] rounded-full relative transition-colors ${autoSave ? "bg-rose-500" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow transition-all ${autoSave ? "left-[18px]" : "left-0.5"}`} />
            </span>
          </button>

          <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200">
            <button onClick={() => setFilter("all")} className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-all ${filter === "all" ? "bg-[#1a2744] text-white shadow" : "text-slate-500 hover:text-slate-800"}`}>
              Load Board
            </button>
            <button onClick={() => setFilter("matched")} className={`flex-1 sm:flex-none justify-center px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${filter === "matched" ? "bg-[#1a2744] text-white shadow" : "text-slate-500 hover:text-slate-800"}`}>
              Priority Match
              {matchedCount > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${filter === 'matched' ? 'bg-amber-400 text-amber-900' : 'bg-amber-100 text-amber-700'}`}>{matchedCount}</span>
              )}
            </button>
          </div>

          <div className="hidden sm:flex bg-white rounded-xl p-1 border border-slate-200">
            <button
              onClick={() => navigate("/my-jobs")}
              className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-all"
            >
              My Jobs
            </button>
            <button
              onClick={() => navigate("/messages")}
              className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-all"
            >
              Messages
            </button>
          </div>
        </div>
      </div>

      {/* LIST OF LOADS */}
      <div className="space-y-4 max-w-4xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            <span className="text-sm font-semibold">Scanning the market...</span>
          </div>
        ) : filteredLoads.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center bg-white rounded-2xl border border-slate-200 p-10 shadow-sm mt-8">
            <Truck className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-slate-700 font-bold">No loads found</p>
            <p className="text-sm text-slate-400 mt-1">Try a different filter or check back later.</p>
          </div>
        ) : (
          filteredLoads.map((load) => {
            const id = load.pk ?? load.id;
            return (
              <LoadRow
                key={id}
                load={load}
                isMatched={load.invited_driver_ids?.includes(user?.id)}
                isUrgent={load.status === 'Urgent'}
                isRevealed={isIdentityRevealed(load)}
                saved={isSaved(id)}
                jobStatus={getJobStatus(id)}
                onToggleSave={() => toggleSave(load)}
                expanded={expandedIds.has(id)}
                onToggleExpand={() => toggleExpand(id)}
                onSubmitOffer={openOfferModal}
                onAcceptJob={handleAcceptJob}
                busy={busyId === id}
              />
            );
          })
        )}
      </div>

      {/* ─── OFFER MODAL ────────────────────────────────────────────────────── */}
      {offerLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/60 sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-black text-slate-900">Submit Offer</h2>
                <p className="text-sm font-semibold text-slate-500 mt-0.5">Ref #{offerLoad.id} · Verified Shipper</p>
              </div>
              <button onClick={() => setOfferLoad(null)} className="p-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 space-y-6">
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-900">Offer type</label>
                <div className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="bid_type"
                      checked={!appForm.is_counter_offer}
                      onChange={() => setAppForm({ ...appForm, is_counter_offer: false, bid_amount: "" })}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm font-bold text-slate-900">Match posted rate ({offerLoad.pay})</span>
                  </label>
                </div>
                <div className={`flex flex-col gap-3 p-4 rounded-xl border transition-colors ${appForm.is_counter_offer ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="bid_type"
                      checked={appForm.is_counter_offer}
                      onChange={() => setAppForm({ ...appForm, is_counter_offer: true })}
                      className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm font-bold text-slate-900">Counter offer</span>
                  </label>
                  {appForm.is_counter_offer && (
                    <div className="pl-7 relative mt-1">
                      <span className="absolute left-10 top-1/2 -translate-y-1/2 text-slate-500 font-bold">$</span>
                      <input
                        type="number"
                        placeholder="Enter your rate"
                        value={appForm.bid_amount}
                        onChange={(e) => setAppForm({ ...appForm, bid_amount: e.target.value })}
                        className="w-full pl-8 pr-4 py-2.5 rounded-lg border-slate-300 focus:border-amber-500 focus:ring-amber-500 text-sm font-bold text-slate-900 shadow-sm"
                        required={appForm.is_counter_offer}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-1.5">Estimated ETA (optional)</label>
                  <input
                    type="datetime-local"
                    value={appForm.estimated_eta}
                    onChange={(e) => setAppForm({ ...appForm, estimated_eta: e.target.value })}
                    className="w-full rounded-xl border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm font-medium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-1.5">Message to shipper (optional)</label>
                  <textarea
                    rows={3}
                    placeholder="E.g., I'm 20 miles away, fully equipped and ready to roll."
                    value={appForm.driver_notes}
                    onChange={(e) => setAppForm({ ...appForm, driver_notes: e.target.value })}
                    className="w-full rounded-xl border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm resize-none"
                  />
                  <p className="text-[11px] text-slate-400 font-medium mt-1.5">Avoid sharing personal contact info here — messaging unlocks once you're awarded the job.</p>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <label className="text-sm font-bold text-slate-900">Equipment verification</label>
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4 shadow-sm">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="pt-0.5">
                      <input
                        type="checkbox"
                        required
                        checked={appForm.confirms_commercial_insurance}
                        onChange={(e) => setAppForm({ ...appForm, confirms_commercial_insurance: e.target.checked })}
                        className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">I confirm I have active commercial insurance</p>
                      <p className="text-xs font-medium text-slate-500 mt-0.5">Required for all pilot car operations.</p>
                    </div>
                  </label>

                  {offerLoad.needs_height_pole && (
                    <label className="flex items-start gap-3 cursor-pointer group pt-4 border-t border-slate-100">
                      <div className="pt-0.5">
                        <input
                          type="checkbox"
                          required
                          checked={appForm.confirms_height_pole}
                          onChange={(e) => setAppForm({ ...appForm, confirms_height_pole: e.target.checked })}
                          className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">I have a functioning height pole</p>
                        <p className="text-xs font-medium text-slate-500 mt-0.5">This load specifically requires height pole equipment.</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-slate-100 bg-slate-50/80 flex gap-3 sticky bottom-0 z-10">
              <button
                type="button"
                onClick={() => setOfferLoad(null)}
                className="flex-1 px-6 py-3 rounded-xl border border-slate-300 text-slate-700 font-bold bg-white hover:bg-slate-50 shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleApplySubmit}
                disabled={isSubmitting || !appForm.confirms_commercial_insurance || (offerLoad.needs_height_pole && !appForm.confirms_height_pole)}
                className="flex-[2] px-6 py-3 rounded-xl bg-[#1a2744] hover:bg-blue-900 text-white font-bold shadow-lg shadow-[#1a2744]/20 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Offer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── TOAST ──────────────────────────────────────────────────────────── */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[60] bg-[#1a2744] text-white px-5 py-3.5 rounded-xl shadow-2xl flex items-center gap-2.5 animate-in fade-in slide-in-from-bottom-2">
          <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
          <span className="text-sm font-bold">{toast.message}</span>
        </div>
      )}
    </div>
  );
}